import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Landmark, Layers,
  Activity, FileSearch, Scale, Cpu, UserCheck, ChevronDown, Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/* STATIC CONTENT                                                      */
/* ------------------------------------------------------------------ */

const WHY_CARDS = [
  { title: 'Regulatory Expectations', copy: 'AI, online-safety, privacy, consumer-protection, and child-safeguarding obligations are evolving rapidly across jurisdictions.' },
  { title: 'Board and Investor Scrutiny', copy: 'Leadership teams are being asked to show who is accountable, what controls exist, and whether those controls can be demonstrated.' },
  { title: 'Incident Exposure', copy: 'A safeguarding failure can create significant legal, regulatory, operational, and reputational consequences.' },
  { title: 'Evidence Takes Time', copy: 'Organisations that begin building a structured evidence record now are better prepared than those reacting later.' },
];

const IS = [
  'A structured preparation programme', 'A current-state diagnostic', 'An evidence-mapping framework',
  'A safeguarding gap review', 'Preparation for independent verification', 'A repeatable and structured methodology',
];
const IS_NOT = [
  'Verification or certification', 'A pass-or-fail test', 'An endorsement or approval',
  'A substitute for independent verification', 'A prediction of a future result', 'An informal consulting opinion',
];

const FIVE_QUESTIONS = [
  'Where are we today?',
  'What are our most important safeguarding risks?',
  'What evidence do we already have?',
  'What evidence is missing?',
  'What should we do next?',
];

const DOMAINS = [
  { icon: Landmark, title: 'Governance', desc: 'Accountability, leadership oversight, policies, ownership, and board-level visibility.' },
  { icon: ShieldCheck, title: 'Safeguarding Architecture', desc: 'The protections designed into the AI system, including age-related controls, content safety, escalation pathways, parental controls, and responses to vulnerable users.' },
  { icon: Activity, title: 'Risk Management', desc: 'How risks to children are identified, tested, monitored, escalated, and managed as the AI system changes.' },
  { icon: FileSearch, title: 'Evidence Readiness', desc: 'Whether the organisation can demonstrate what it does through documentation, records, audit trails, testing evidence, and retained decision history.' },
  { icon: Scale, title: 'Regulatory Compliance', desc: 'The obligations that may apply across the organisation\u2019s operating jurisdictions and its current compliance posture.' },
];

const MATURITY = ['Absent', 'Nascent', 'Developing', 'Established', 'Assured'];

const STEPS = [
  { n: '1', title: 'Apply for the Programme', desc: 'Tell SAFE about your organisation, AI system, objectives, timing, and likely programme needs.' },
  { n: '2', title: 'Scope Confirmation', desc: 'SAFE reviews the application and confirms the appropriate programme tier, scope, and final price.' },
  { n: '3', title: 'Detailed Programme Intake', desc: 'Once the engagement is confirmed, the organisation completes the detailed intake questionnaire and submits relevant documentation.' },
  { n: '4', title: 'Structured Analysis and Review', desc: 'Submitted materials are analysed across the five programme domains and validated by a qualified reviewer.' },
  { n: '5', title: 'Receive the Readiness Report', desc: 'The organisation receives its findings, evidence gap register, priority actions, and readiness determination.' },
  { n: '6', title: 'Begin Improving', desc: 'The organisation works through the recommended actions and decides when it is ready to consider future SAFE Labs verification.' },
];

const DELIVERABLES = [
  'Executive Summary', 'Safeguarding Posture', 'Domain Maturity Findings', 'Critical Risk Findings',
  'Evidence Inventory', 'Evidence Gap Register', 'Priority Actions', 'Immediate, 30-Day, and 90-Day Actions',
  'Roadmap to Verification Readiness', 'Regulatory Exposure Review', 'Verification Readiness Determination',
  'Qualified Reviewer Debrief',
];

const DETERMINATIONS = [
  { title: 'Significant Development Required', desc: 'Material gaps exist across multiple domains. The organisation should address the Priority Actions before entering independent verification.' },
  { title: 'Approaching Readiness', desc: 'Meaningful safeguarding foundations are in place, but focused improvement and evidence development are still required.' },
  { title: 'Readiness Viable', desc: 'The organisation has a strong current position, with remaining work primarily involving evidence formalisation and targeted improvement.' },
];

const AI_ASSISTS = ['Document classification', 'Evidence indexing', 'Domain mapping', 'Consistency checks', 'Gap identification', 'Draft finding preparation'];
const REVIEWERS = ['Validate material findings', 'Confirm critical risks', 'Confirm domain maturity characterisations', 'Write the Executive Summary', 'Finalise Priority Actions', 'Issue the readiness determination', 'Conduct the client debrief', 'Approve the report'];

const TIERS = [
  {
    name: 'Essential', value: 'Essential', price: 'Starting from US$12,000', badge: null,
    bestFor: 'Early-stage organisations, a single AI product, limited documentation, or a first structured assessment.',
    includes: ['Full Verification Readiness Programme', 'Assessment across all five domains', 'Readiness Report', 'Evidence Inventory', 'Evidence Gap Register', 'Priority Actions', 'Readiness determination', '60-minute reviewer debrief'],
    cta: 'Apply for Essential',
  },
  {
    name: 'Standard', value: 'Standard', price: 'Starting from US$18,000', badge: 'Most Popular',
    bestFor: 'Growth-stage organisations, multiple AI features or user cohorts, and organisations with existing governance, risk, or safeguarding documentation.',
    includes: ['Everything included in Essential', 'Expanded regulatory exposure analysis', 'Broader evidence review', 'Additional consideration of operating jurisdictions and user cohorts', '60-minute reviewer debrief'],
    cta: 'Apply for Standard',
  },
  {
    name: 'Enterprise', value: 'Enterprise', price: 'Starting from US$28,000', badge: null,
    bestFor: 'Large platforms, complex AI systems, multiple products, multiple jurisdictions, group-wide reviews, or enterprise procurement.',
    includes: ['Custom programme scope', 'Multiple AI products or systems where agreed', 'Two qualified reviewers where required', 'Expanded jurisdictional analysis', '90-minute reviewer debrief', 'Dedicated programme contact', 'Enterprise onboarding support'],
    cta: 'Request Enterprise Scope',
  },
];

const WHO_SHOULD_APPLY = [
  'An AI system is designed for children.',
  'Children are likely to use the system, even if they are not the primary audience.',
  'The system materially affects children.',
  'The organisation is preparing for board, investor, insurer, procurement, or regulatory scrutiny.',
  'Existing safeguarding claims need to be supported by evidence.',
  'The organisation wants a structured improvement pathway before independent verification.',
  'The organisation operates across multiple regulatory jurisdictions.',
  'The organisation wants to prepare before an incident or formal review occurs.',
];

const FAQS = [
  { q: 'Is this SAFE Labs verification?', a: 'No. The Verification Readiness Programme is a preparation programme. It helps an organisation understand its current position and prepare for a future independent process. It is not verification, certification, endorsement, or approval.' },
  { q: 'Does completing the programme guarantee verification?', a: 'No. Completing the programme can improve an organisation\u2019s preparedness, but SAFE Labs independently determines future verification outcomes.' },
  { q: 'What happens if we have limited documentation?', a: 'The programme assesses what exists. Missing documentation becomes part of the Evidence Gap Register and improvement roadmap.' },
  { q: 'How much time will our team need to commit?', a: 'The initial public application should take approximately 8\u201312 minutes. The detailed programme intake, completed after scope confirmation, is expected to take approximately 45\u201360 minutes.' },
  { q: 'Who reviews our information?', a: 'Technology assists with classification, mapping, and draft analysis. A qualified human reviewer validates material findings and approves the final report.' },
  { q: 'How long does the programme take?', a: 'Timing depends on scope, completeness of information, evidence volume, and reviewer availability. A delivery schedule is confirmed after the application has been reviewed.' },
  { q: 'Can the report be shared with our board or investors?', a: 'Yes. The report is designed to support internal governance and informed commercial discussions. It must not be represented as SAFE Labs verification or certification.' },
  { q: 'Can we apply before SAFE Labs verification is formally available?', a: 'Yes. Building governance, safeguarding, risk-management, and evidence foundations early is one of the programme\u2019s principal benefits.' },
];

/* ------------------------------------------------------------------ */
/* FORM OPTIONS                                                        */
/* ------------------------------------------------------------------ */

const CONTACT_METHODS = ['Email', 'Phone', 'Video call'];
const ORG_TYPES = ['AI product company', 'Technology platform', 'Education technology', 'Gaming or social platform', 'Digital health', 'Connected product or AI toy', 'School or education provider', 'Government or public sector', 'Enterprise procuring AI', 'Investor or portfolio organisation', 'Non-profit or research organisation', 'Other'];
const ORG_SIZES = ['1\u201310', '11\u201350', '51\u2013200', '201\u2013500', '501\u20131,000', '1,001\u20135,000', '5,000+'];
const DEV_STAGES = ['Concept', 'Prototype', 'Pilot', 'Limited release', 'Publicly available', 'Mature product', 'Multiple products or systems'];
const DIRECT_INTERACTION = ['Yes', 'No', 'Potentially', 'Unknown'];
const CHILD_AUDIENCE = ['Primary audience', 'Part of the audience', 'Not intended, but access is possible', 'The system affects children indirectly', 'Unknown'];
const AGE_GROUPS = ['Under 5', '5\u20137', '8\u201312', '13\u201315', '16\u201317', 'Unknown or mixed'];
const CHILD_RANGES = ['Pre-launch', 'Fewer than 1,000', '1,000\u201310,000', '10,001\u2013100,000', '100,001\u20131 million', 'More than 1 million', 'Unknown'];
const AI_FUNCTIONS = ['Conversational AI or chatbot', 'Generative text', 'Image generation', 'Audio or voice interaction', 'Video generation or analysis', 'Recommendation system', 'Moderation or content filtering', 'Education or tutoring', 'Health or wellbeing', 'Behavioural profiling', 'Automated decision-making', 'Social or companion experience', 'Connected device or physical product', 'Other'];
const POLICY_OPTIONS = ['Yes', 'No', 'In progress', 'Unsure'];
const DOC_QUANTITY = ['Very limited', 'Some documents', 'A moderate evidence base', 'Extensive documentation', 'Unsure'];
const TRIGGERS = ['Preparing for future SAFE Labs verification', 'Board request', 'Investor due diligence', 'Customer or procurement request', 'Regulatory preparation', 'Recent incident or near miss', 'New product launch', 'Expansion into a new market', 'Insurance or risk-management requirement', 'Internal governance initiative', 'Other'];
const TIMINGS = ['As soon as possible', 'Within 30 days', 'Within 60 days', 'Within 90 days', 'More than 90 days', 'Exploratory only'];
const PROGRAMME_OPTIONS = ['Essential \u2014 starting from US$12,000', 'Standard \u2014 starting from US$18,000', 'Enterprise \u2014 starting from US$28,000', 'Unsure \u2014 please recommend a programme'];
const BUDGETS = ['Under US$12,000', 'US$12,000\u2013US$17,999', 'US$18,000\u2013US$27,999', 'US$28,000\u2013US$49,999', 'US$50,000\u2013US$99,999', 'US$100,000+', 'Budget not yet approved', 'Prefer not to say'];
const REFERRALS = ['Search engine', 'LinkedIn', 'SAFE Council', 'Referral', 'Conference or event', 'Media', 'Partner', 'Existing relationship', 'Other'];

const inputClass = 'w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 transition-colors bg-white';

const initialForm = {
  first_name: '', last_name: '', work_email: '', phone: '', job_title: '', preferred_contact_method: '',
  legal_org_name: '', trading_name: '', website: '', hq_country: '', operating_jurisdictions: '', org_type: '', org_size: '',
  product_name: '', product_url: '', product_description: '', development_stage: '', direct_interaction: '', child_audience: '', age_groups: [], child_user_range: '', ai_functionality: [],
  has_governance_policy: '', has_safeguarding_policy: '', child_risk_assessment: '', escalation_documented: '', documentation_quantity: '', current_concerns: '',
  application_trigger: '', success_outcome: '', desired_start_date: '', specific_deadline: '', programme_of_interest: '', indicative_budget: '', referral_source: '', additional_information: '',
  declaration_authorised: false, declaration_not_verification: false, declaration_no_guarantee: false, declaration_privacy: false, marketing_opt_in: false,
  company_fax: '',
};

const STEP_LABELS = ['Contact', 'Organisation', 'AI System', 'Current Position', 'Objectives'];
const DRAFT_KEY = 'safe_vrp_application_draft';

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1.5">{hint}</p>}
  </div>
);

const Select = ({ name, value, onChange, options, placeholder, testid }) => (
  <select name={name} value={value} onChange={onChange} className={inputClass} data-testid={testid}>
    <option value="">{placeholder || 'Select\u2026'}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const CheckGroup = ({ options, selected, onToggle, prefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
    {options.map((o) => {
      const checked = selected.includes(o);
      return (
        <label key={o} className={`flex items-center gap-3 border px-4 py-2.5 cursor-pointer transition-colors ${checked ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400'}`} data-testid={`${prefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          <input type="checkbox" checked={checked} onChange={() => onToggle(o)} className="w-4 h-4 accent-slate-900" />
          <span className="text-sm text-slate-700">{o}</span>
        </label>
      );
    })}
  </div>
);

/* ------------------------------------------------------------------ */
/* MAIN PAGE                                                           */
/* ------------------------------------------------------------------ */

const VerificationReadinessPage = () => {
  const [formData, setFormData] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [draftId, setDraftId] = useState('');
  const [draftSavedAt, setDraftSavedAt] = useState('');
  const formRef = useRef(null);

  // Pick up an unfinished application from a previous visit.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved?.formData?.work_email) {
        setFormData((p) => ({ ...p, ...saved.formData, company_fax: '' }));
        setDraftId(saved.draftId || '');
        setStep(Math.min(5, Math.max(1, saved.step || 1)));
        if (saved.draftId) setDraftSavedAt('restored');
      }
    } catch (e) { /* a corrupt draft must never block the form */ }
  }, []);

  // SEO: title, meta description, JSON-LD schema
  useEffect(() => {
    document.title = 'AI Verification Readiness Programme | SAFE Labs';
    const metaDesc = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); return m;
    })();
    const prevDesc = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', 'Understand your organisation\u2019s child AI safeguarding position, evidence gaps, priority actions, and readiness for future SAFE Labs independent verification.');

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify([
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://safestandard.ai/' },
          { '@type': 'ListItem', position: 2, name: 'SAFE Labs', item: 'https://safestandard.ai/labs' },
          { '@type': 'ListItem', position: 3, name: 'Verification Readiness', item: 'https://safestandard.ai/safe-labs/verification-readiness' },
        ],
      },
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      },
    ]);
    document.head.appendChild(ld);
    return () => {
      if (prevDesc) metaDesc.setAttribute('content', prevDesc);
      document.head.removeChild(ld);
    };
  }, []);

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  const toggleArr = (field, value) => setFormData((p) => ({
    ...p, [field]: p[field].includes(value) ? p[field].filter((v) => v !== value) : [...p[field], value],
  }));

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const selectTier = (tierValue, programmeLabel) => {
    setFormData((p) => ({ ...p, programme_of_interest: programmeLabel }));
    setStep(1);
    setTimeout(scrollToForm, 50);
    toast.success(`${tierValue === 'Enterprise' ? 'Enterprise scope' : tierValue + ' programme'} selected \u2014 complete the application below.`);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.first_name || !formData.last_name || !formData.work_email || !formData.job_title) {
        toast.error('Please complete all required fields (marked *).');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.work_email)) {
        toast.error('Please enter a valid work email address.');
        return false;
      }
    }
    if (step === 2 && !formData.legal_org_name) {
      toast.error('Please enter your legal organisation name.');
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    saveProgress(step);
    setStep((s) => Math.min(5, s + 1));
    scrollToForm();
  };
  const back = () => { setStep((s) => Math.max(1, s - 1)); scrollToForm(); };

  const collectUtm = () => {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
      if (params.get(k)) utm[k] = params.get(k);
    });
    return utm;
  };

  const tierFromProgramme = () => TIERS.find((t) => formData.programme_of_interest.startsWith(t.name))?.value
    || (formData.programme_of_interest.startsWith('Unsure') ? 'Unsure' : '');

  // Contact details are captured as soon as step 1 is valid, then the same record
  // is updated on every step, so an abandoned application is still a real lead.
  const saveProgress = async (completedStep) => {
    if (!formData.work_email) return;
    try {
      const payload = {
        ...formData, draft_id: draftId, last_step: completedStep,
        selected_tier: tierFromProgramme(),
        source_page: '/safe-labs/verification-readiness', utm: collectUtm(),
      };
      const res = await axios.post(`${API}/forms/verification-readiness/partial`, payload);
      const id = res.data?.draft_id || draftId;
      if (id) setDraftId(id);
      setDraftSavedAt(new Date().toISOString());
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        draftId: id, step: Math.min(5, completedStep + 1), formData: { ...formData, company_fax: '' },
      }));
    } catch (err) {
      // Never block progress on a background save.
      console.error('Draft save failed', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.declaration_authorised || !formData.declaration_not_verification || !formData.declaration_no_guarantee || !formData.declaration_privacy) {
      toast.error('Please confirm all required declarations to submit.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData, selected_tier: tierFromProgramme(), draft_id: draftId,
        source_page: '/safe-labs/verification-readiness', utm: collectUtm(),
      };
      const res = await axios.post(`${API}/forms/verification-readiness`, payload);
      if (res.data?.success) {
        localStorage.removeItem(DRAFT_KEY);
        setResult({ reference: res.data.id, programme: formData.programme_of_interest || 'To be recommended by SAFE', email: formData.work_email });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- SUCCESS SCREEN ---------------- */
  if (result) {
    return (
      <div className="bg-slate-50 min-h-screen" data-testid="vrp-success">
        <section className="bg-slate-900 text-white py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
            <p className="text-sm text-cyan-400 uppercase tracking-widest font-semibold mb-3">Application Received</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">Your Application Has Been Received</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Thank you. SAFE will review your organisation, AI system, objectives, requested timeframe, and likely
              programme scope. A member of the team will contact you regarding the recommended next step.
            </p>
          </div>
        </section>
        <section className="py-14">
          <div className="max-w-xl mx-auto px-6 md:px-12">
            <div className="bg-white border border-slate-200 divide-y divide-slate-200" data-testid="vrp-success-details">
              <div className="flex justify-between px-6 py-4"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Application reference</span><span className="text-sm font-bold text-slate-900" data-testid="vrp-reference">{result.reference}</span></div>
              <div className="flex justify-between px-6 py-4"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Selected programme</span><span className="text-sm text-slate-900">{result.programme}</span></div>
              <div className="flex justify-between px-6 py-4"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Applicant email</span><span className="text-sm text-slate-900">{result.email}</span></div>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed mt-6">
              Submitting an application does not commit your organisation to purchase the programme. The final scope and
              price will be confirmed before any payment or contractual commitment.
            </p>
            <Link to="/labs" className="mt-8 inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors" data-testid="vrp-return-labs">
              Return to SAFE Labs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  /* ---------------- PAGE ---------------- */
  return (
    <div className="bg-slate-50 min-h-screen" data-testid="verification-readiness-page">
      {/* BREADCRUMBS */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 text-xs text-slate-500 flex items-center gap-2" data-testid="vrp-breadcrumbs">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <span>/</span>
          <Link to="/labs" className="hover:text-slate-900">SAFE Labs</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">Verification Readiness</span>
        </div>
      </div>

      {/* SECTION 1 — HERO */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-sm text-cyan-400 uppercase tracking-[0.2em] font-semibold mb-5">SAFE Labs | Verification Readiness</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-7">
              Know Where You Stand Before Independent Verification Begins
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              The SAFE Verification Readiness Programme gives organisations building or operating AI that affects children
              a clear, expert-reviewed view of their current safeguarding position, evidence gaps, priority actions, and
              pathway toward future independent verification.
            </p>
            <div className="flex flex-wrap gap-4 mb-6">
              <button onClick={() => { setStep(1); scrollToForm(); }} className="inline-flex items-center gap-2 bg-cyan-600 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-cyan-500 transition-colors" data-testid="vrp-hero-apply">
                Apply for the Programme <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#how-it-works" className="inline-flex items-center gap-2 border border-slate-600 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors" data-testid="vrp-hero-how">
                See How It Works
              </a>
            </div>
            <p className="text-sm text-slate-400 mb-4">AI-assisted analysis. Qualified human review. Clear, evidence-based actions.</p>
            <p className="text-xs text-slate-500 border-l-2 border-slate-700 pl-4 leading-relaxed max-w-lg">
              This is a preparation programme. It is not verification, certification, endorsement, or a guarantee of a
              future SAFE Labs outcome.
            </p>
          </div>
          {/* Visual placeholder — readiness pathway (replaceable) */}
          <div className="hidden lg:block" data-testid="vrp-hero-visual">
            <div className="border border-slate-700 bg-slate-800/40 p-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-6">Readiness Pathway</p>
              <div className="space-y-3">
                {MATURITY.map((m, i) => (
                  <div key={m} className="flex items-center gap-4">
                    <span className="text-xs text-slate-500 w-6">{i + 1}</span>
                    <div className="flex-1 h-9 border border-slate-700 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-cyan-500/20 border-r border-cyan-500/50" style={{ width: `${((i + 1) / MATURITY.length) * 100}%` }} />
                      <span className="absolute inset-0 flex items-center px-3 text-sm text-slate-200">{m}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-6">Illustrative. Final graphic to follow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY ORGANISATIONS NEED THIS */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-5">The Questions Are Already Being Asked</h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mb-12">
            Boards, investors, regulators, procurement teams, insurers, and commercial partners increasingly expect
            organisations to explain how AI-related risks to children are being governed and evidenced.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-12">
            {WHY_CARDS.map((c) => (
              <div key={c.title} className="bg-white p-7">
                <h3 className="text-base font-semibold text-slate-900 mb-3">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.copy}</p>
              </div>
            ))}
          </div>
          <button onClick={() => { setStep(1); scrollToForm(); }} className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-900 border-b-2 border-cyan-600 pb-1 hover:text-cyan-700 transition-colors" data-testid="vrp-cta-position">
            Understand Your Current Position <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* SECTION 3 — WHAT THE PROGRAMME IS */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-5">Preparation Before Verification</h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mb-14">
            The Verification Readiness Programme sits between an organisation&rsquo;s current position and the point at which
            it decides to enter SAFE Labs independent verification. It provides a structured current-state diagnostic,
            evidence review, gap analysis, and practical improvement roadmap.
          </p>
          <div className="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-700 mb-6">The Programme Is</p>
              <ul className="space-y-3">
                {IS.map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700"><Check className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />{i}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">The Programme Is Not</p>
              <ul className="space-y-3">
                {IS_NOT.map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-500"><span className="w-4 h-4 flex-shrink-0 mt-0.5 flex items-center justify-center text-slate-400">&times;</span>{i}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-lg md:text-xl font-semibold text-slate-900 mt-10 border-l-2 border-cyan-600 pl-5">The purpose is improvement, not judgment.</p>
        </div>
      </section>

      {/* SECTION 4 — FIVE QUESTIONS */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-12">Five Questions Every Organisation Needs to Answer</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-slate-200 border border-slate-200 mb-10">
            {FIVE_QUESTIONS.map((q, i) => (
              <div key={q} className="bg-white p-6">
                <span className="text-3xl font-bold text-cyan-600/40">{i + 1}</span>
                <p className="text-sm text-slate-800 leading-relaxed mt-3 font-medium">{q}</p>
              </div>
            ))}
          </div>
          <p className="text-base text-slate-600 leading-relaxed max-w-3xl">
            The programme is designed to give leadership, product, legal, risk, governance, and trust-and-safety teams
            clear answers they can act on.
          </p>
        </div>
      </section>

      {/* SECTION 5 — FIVE DOMAINS */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-12">A Complete View Across Five Domains</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {DOMAINS.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.title} className="bg-white p-8">
                  <Icon className="w-8 h-8 text-cyan-700 mb-5" strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{d.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
            <div className="bg-slate-50 p-8 flex flex-col justify-center">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Maturity Characterisation</p>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">Each domain receives a maturity characterisation rather than a simplistic composite score.</p>
              <div className="flex flex-wrap gap-2">
                {MATURITY.map((m) => <span key={m} className="text-xs font-medium text-slate-700 bg-white border border-slate-300 px-3 py-1.5">{m}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — HOW IT WORKS */}
      <section id="how-it-works" className="py-20 md:py-28 border-b border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">A Clear Path From Intake to Action</h2>
          <p className="text-base text-slate-500 mb-14 max-w-2xl">Six steps from application through to acting on your findings.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white p-8">
                <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center text-lg font-bold mb-5">{s.n}</div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHAT YOU RECEIVE */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">A Practical Report Built for Action</h2>
            <p className="text-base text-slate-600 leading-relaxed mb-8">
              The report is designed to be useful across the boardroom, executive team, legal and risk functions, product
              teams, trust and safety teams, and technical leadership.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {DELIVERABLES.map((d) => (
                <div key={d} className="flex items-start gap-3 text-sm text-slate-700"><Check className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />{d}</div>
              ))}
            </div>
          </div>
          {/* Report mock-up placeholder */}
          <div className="border border-slate-200 bg-slate-50 p-8 flex flex-col" data-testid="vrp-report-visual">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-6">Report Preview</p>
            <div className="bg-white border border-slate-200 p-6 flex-1 space-y-4">
              <div className="h-3 w-1/3 bg-slate-900" />
              <div className="h-2 w-full bg-slate-200" />
              <div className="h-2 w-5/6 bg-slate-200" />
              <div className="grid grid-cols-5 gap-2 pt-4">
                {MATURITY.map((m, i) => <div key={m} className="h-10 bg-cyan-500/10 border border-cyan-500/30" style={{ opacity: 0.4 + i * 0.12 }} />)}
              </div>
              <div className="h-2 w-2/3 bg-slate-200" />
              <div className="h-2 w-1/2 bg-slate-200" />
            </div>
            <p className="text-[11px] text-slate-400 mt-6">Illustrative layout. Final report visual to follow.</p>
          </div>
        </div>
      </section>

      {/* SECTION 8 — READINESS DETERMINATIONS */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-12">A Clear View of How Much Work Remains</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mb-8">
            {DETERMINATIONS.map((d) => (
              <div key={d.title} className="bg-white p-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{d.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 leading-relaxed max-w-3xl border-l-2 border-slate-300 pl-5">
            These determinations describe readiness to enter a future verification process. They do not determine or
            predict the outcome of SAFE Labs independent verification.
          </p>
        </div>
      </section>

      {/* SECTION 9 — HUMAN OVERSIGHT */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <Cpu className="w-7 h-7 text-cyan-400" strokeWidth={1.5} />
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">AI-Assisted. Human-Reviewed.</h2>
          </div>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mb-12">
            Technology is used to accelerate document classification, evidence mapping, cross-domain analysis, and
            structured draft findings. Qualified reviewers remain responsible for professional judgment.
          </p>
          <div className="grid md:grid-cols-2 gap-px bg-slate-700 border border-slate-700">
            <div className="bg-slate-900 p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-6">AI Assists With</p>
              <ul className="space-y-3">
                {AI_ASSISTS.map((a) => <li key={a} className="flex items-start gap-3 text-sm text-slate-300"><span className="w-1.5 h-1.5 bg-cyan-400 flex-shrink-0 mt-2" />{a}</li>)}
              </ul>
            </div>
            <div className="bg-slate-900 p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-white mb-6 flex items-center gap-2"><UserCheck className="w-4 h-4" /> Qualified Reviewers</p>
              <ul className="space-y-3">
                {REVIEWERS.map((r) => <li key={r} className="flex items-start gap-3 text-sm text-slate-300"><Check className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />{r}</li>)}
              </ul>
            </div>
          </div>
          <p className="text-lg md:text-xl font-semibold mt-10 border-l-2 border-cyan-500 pl-5">No report leaves SAFE without qualified human review.</p>
        </div>
      </section>

      {/* SECTION 10 — TIERS */}
      <section id="programme-tiers" className="py-20 md:py-28 border-b border-slate-200 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-5">Choose the Programme That Matches Your Organisation</h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mb-14">
            Every engagement is scoped according to the organisation, AI system, operating jurisdictions, available
            evidence, and level of complexity. The prices below indicate the starting point for each programme tier.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {TIERS.map((t) => (
              <div key={t.name} className={`relative bg-white border p-8 flex flex-col ${t.badge ? 'border-slate-900 shadow-lg' : 'border-slate-200'}`} data-testid={`vrp-tier-${t.value.toLowerCase()}`}>
                {t.badge && <span className="absolute -top-3 left-8 bg-cyan-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1">{t.badge}</span>}
                <h3 className="text-xl font-bold text-slate-900 mb-2">{t.name}</h3>
                <p className="text-lg font-semibold text-cyan-700 mb-4">{t.price}</p>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{t.bestFor}</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {t.includes.map((inc) => <li key={inc} className="flex items-start gap-2.5 text-sm text-slate-700"><Check className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />{inc}</li>)}
                </ul>
                <button onClick={() => selectTier(t.value, PROGRAMME_OPTIONS.find((p) => p.startsWith(t.name)))} className={`w-full px-6 py-3.5 text-sm font-bold uppercase tracking-widest transition-colors ${t.badge ? 'bg-cyan-600 text-white hover:bg-cyan-500' : 'bg-slate-900 text-white hover:bg-slate-800'}`} data-testid={`vrp-tier-cta-${t.value.toLowerCase()}`}>
                  {t.cta}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-slate-50 border border-slate-200 p-6">
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              <span className="font-semibold text-slate-900">Not sure which programme applies?</span> Complete the initial
              application and SAFE will recommend the most appropriate scope before any commitment is made.
            </p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Prices are shown in United States dollars and represent starting prices only. Final pricing is confirmed
              after SAFE reviews the organisation&rsquo;s scope, AI system, operating jurisdictions, evidence volume,
              complexity, and requested timeframe. Applicable taxes may be added.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 11 — WHO SHOULD APPLY */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">Is This Programme Right for Your Organisation?</h2>
          <p className="text-base text-slate-500 mb-10">The programme may be appropriate where:</p>
          <ul className="space-y-3 mb-12">
            {WHO_SHOULD_APPLY.map((w) => (
              <li key={w} className="flex items-start gap-3 bg-white border border-slate-200 px-5 py-4 text-sm text-slate-700"><Check className="w-5 h-5 text-cyan-600 flex-shrink-0" />{w}</li>
            ))}
          </ul>
          <button onClick={() => { setStep(1); scrollToForm(); }} className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors" data-testid="vrp-cta-start-checklist">
            Start the Application <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* SECTION 12 — FAQ */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-12">Frequently Asked Questions</h2>
          <div className="border-t border-slate-200">
            {FAQS.map((f, i) => (
              <div key={f.q} className="border-b border-slate-200">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left" data-testid={`vrp-faq-${i}`}>
                  <span className="text-base font-semibold text-slate-900">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="text-sm text-slate-600 leading-relaxed pb-5 pr-8" data-testid={`vrp-faq-answer-${i}`}>{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 13 — FINAL CTA */}
      <section className="py-20 md:py-28 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">Start Building Your Verification Readiness</h2>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-10 max-w-2xl mx-auto">
            Understand your current position, identify the evidence and safeguarding gaps that matter most, and establish
            a clear pathway toward future independent verification.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-6">
            <button onClick={() => { setStep(1); scrollToForm(); }} className="inline-flex items-center gap-2 bg-cyan-600 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-cyan-500 transition-colors" data-testid="vrp-final-apply">
              Apply for the Programme <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => selectTier('Enterprise', PROGRAMME_OPTIONS.find((p) => p.startsWith('Enterprise')))} className="inline-flex items-center gap-2 border border-slate-600 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors" data-testid="vrp-final-enterprise">
              Request Enterprise Scope
            </button>
          </div>
          <p className="text-xs text-slate-500">Submitting an application does not commit your organisation to purchase.</p>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section ref={formRef} id="apply" className="py-20 md:py-28 scroll-mt-20" data-testid="vrp-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-sm text-cyan-700 uppercase tracking-widest font-semibold mb-3">Application</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3">Apply for the Programme</h2>
          <p className="text-base text-slate-500 mb-4">This short application takes approximately 8&ndash;12 minutes. Fields marked with * are required.</p>
          <p className="text-sm text-slate-600 mb-10 border-l-2 border-cyan-600 pl-4" data-testid="vrp-save-notice">
            <span className="font-semibold text-slate-900">Your progress is saved at every step.</span> Once you have
            completed your contact details you can close this page and come back to finish the application later &mdash;
            nothing is submitted until you complete the final step.
          </p>

          {/* Progress indicator */}
          <div className="flex items-center gap-1 mb-10" data-testid="vrp-progress">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              return (
                <div key={label} className="flex-1">
                  <div className={`h-1 mb-2 ${done || active ? 'bg-cyan-600' : 'bg-slate-200'}`} />
                  <p className={`text-xs font-medium ${active ? 'text-slate-900' : done ? 'text-cyan-700' : 'text-slate-400'}`}>{n}. {label}</p>
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="vrp-form">
            {/* Honeypot */}
            <input type="text" name="company_fax" value={formData.company_fax} onChange={handleChange} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            {/* STEP 1 — CONTACT */}
            {step === 1 && (
              <div className="bg-white border border-slate-200 p-6 md:p-8" data-testid="vrp-step-1">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Your Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="First name" required><input name="first_name" value={formData.first_name} onChange={handleChange} className={inputClass} data-testid="vrp-first-name" /></Field>
                  <Field label="Last name" required><input name="last_name" value={formData.last_name} onChange={handleChange} className={inputClass} data-testid="vrp-last-name" /></Field>
                  <Field label="Work email" required><input type="email" name="work_email" value={formData.work_email} onChange={handleChange} className={inputClass} data-testid="vrp-work-email" /></Field>
                  <Field label="Phone number"><input name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-testid="vrp-phone" /></Field>
                  <Field label="Job title" required><input name="job_title" value={formData.job_title} onChange={handleChange} className={inputClass} data-testid="vrp-job-title" /></Field>
                  <Field label="Preferred contact method"><Select name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleChange} options={CONTACT_METHODS} testid="vrp-contact-method" /></Field>
                </div>
              </div>
            )}

            {/* STEP 2 — ORGANISATION */}
            {step === 2 && (
              <div className="bg-white border border-slate-200 p-6 md:p-8" data-testid="vrp-step-2">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Your Organisation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Legal organisation name" required><input name="legal_org_name" value={formData.legal_org_name} onChange={handleChange} className={inputClass} data-testid="vrp-legal-name" /></Field>
                  <Field label="Trading name"><input name="trading_name" value={formData.trading_name} onChange={handleChange} className={inputClass} data-testid="vrp-trading-name" /></Field>
                  <Field label="Website"><input name="website" value={formData.website} onChange={handleChange} className={inputClass} placeholder="https://…" data-testid="vrp-website" /></Field>
                  <Field label="Headquarters country"><input name="hq_country" value={formData.hq_country} onChange={handleChange} className={inputClass} data-testid="vrp-hq-country" /></Field>
                  <Field label="Primary operating jurisdictions" hint="e.g. UK, EU, US, Australia"><input name="operating_jurisdictions" value={formData.operating_jurisdictions} onChange={handleChange} className={inputClass} data-testid="vrp-jurisdictions" /></Field>
                  <Field label="Organisation type"><Select name="org_type" value={formData.org_type} onChange={handleChange} options={ORG_TYPES} testid="vrp-org-type" /></Field>
                  <Field label="Organisation size"><Select name="org_size" value={formData.org_size} onChange={handleChange} options={ORG_SIZES} testid="vrp-org-size" /></Field>
                </div>
              </div>
            )}

            {/* STEP 3 — AI PRODUCT */}
            {step === 3 && (
              <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5" data-testid="vrp-step-3">
                <h3 className="text-lg font-bold text-slate-900 mb-2">AI Product or System</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Product or AI system name"><input name="product_name" value={formData.product_name} onChange={handleChange} className={inputClass} data-testid="vrp-product-name" /></Field>
                  <Field label="Product URL"><input name="product_url" value={formData.product_url} onChange={handleChange} className={inputClass} placeholder="https://…" data-testid="vrp-product-url" /></Field>
                </div>
                <Field label="Brief product description"><textarea name="product_description" rows={3} value={formData.product_description} onChange={handleChange} className={inputClass} data-testid="vrp-product-desc" /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Current development stage"><Select name="development_stage" value={formData.development_stage} onChange={handleChange} options={DEV_STAGES} testid="vrp-dev-stage" /></Field>
                  <Field label="Does the system interact directly with children?"><Select name="direct_interaction" value={formData.direct_interaction} onChange={handleChange} options={DIRECT_INTERACTION} testid="vrp-direct-interaction" /></Field>
                  <Field label="Are children the intended users?"><Select name="child_audience" value={formData.child_audience} onChange={handleChange} options={CHILD_AUDIENCE} testid="vrp-child-audience" /></Field>
                  <Field label="Estimated number of child users / children affected"><Select name="child_user_range" value={formData.child_user_range} onChange={handleChange} options={CHILD_RANGES} testid="vrp-child-range" /></Field>
                </div>
                <Field label="Age groups potentially affected" hint="Select all that apply"><CheckGroup options={AGE_GROUPS} selected={formData.age_groups} onToggle={(v) => toggleArr('age_groups', v)} prefix="vrp-age" /></Field>
                <Field label="AI functionality" hint="Select all that apply"><CheckGroup options={AI_FUNCTIONS} selected={formData.ai_functionality} onToggle={(v) => toggleArr('ai_functionality', v)} prefix="vrp-fn" /></Field>
              </div>
            )}

            {/* STEP 4 — CURRENT POSITION */}
            {step === 4 && (
              <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5" data-testid="vrp-step-4">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Current Position</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Documented AI governance policy?"><Select name="has_governance_policy" value={formData.has_governance_policy} onChange={handleChange} options={POLICY_OPTIONS} testid="vrp-gov-policy" /></Field>
                  <Field label="Documented child safeguarding policy?"><Select name="has_safeguarding_policy" value={formData.has_safeguarding_policy} onChange={handleChange} options={POLICY_OPTIONS} testid="vrp-safeguarding-policy" /></Field>
                  <Field label="Child-specific risk assessment completed?"><Select name="child_risk_assessment" value={formData.child_risk_assessment} onChange={handleChange} options={POLICY_OPTIONS} testid="vrp-risk-assessment" /></Field>
                  <Field label="Escalation & incident-response pathways documented?"><Select name="escalation_documented" value={formData.escalation_documented} onChange={handleChange} options={POLICY_OPTIONS} testid="vrp-escalation" /></Field>
                  <Field label="Approximate quantity of relevant documentation"><Select name="documentation_quantity" value={formData.documentation_quantity} onChange={handleChange} options={DOC_QUANTITY} testid="vrp-doc-quantity" /></Field>
                </div>
                <Field label="Current concerns or known gaps" hint="Optional"><textarea name="current_concerns" rows={4} value={formData.current_concerns} onChange={handleChange} className={inputClass} data-testid="vrp-concerns" /></Field>
              </div>
            )}

            {/* STEP 5 — OBJECTIVES + DECLARATIONS */}
            {step === 5 && (
              <div className="space-y-8" data-testid="vrp-step-5">
                <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Objectives &amp; Commercial Fit</h3>
                  <Field label="What prompted the organisation to apply?"><Select name="application_trigger" value={formData.application_trigger} onChange={handleChange} options={TRIGGERS} testid="vrp-trigger" /></Field>
                  <Field label="What would a successful outcome look like?"><textarea name="success_outcome" rows={3} value={formData.success_outcome} onChange={handleChange} className={inputClass} data-testid="vrp-success-outcome" /></Field>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Desired programme start date"><Select name="desired_start_date" value={formData.desired_start_date} onChange={handleChange} options={TIMINGS} testid="vrp-start-date" /></Field>
                    <Field label="Is there a specific deadline?"><input name="specific_deadline" value={formData.specific_deadline} onChange={handleChange} className={inputClass} placeholder="e.g. Board meeting in Sept" data-testid="vrp-deadline" /></Field>
                    <Field label="Programme of interest"><Select name="programme_of_interest" value={formData.programme_of_interest} onChange={handleChange} options={PROGRAMME_OPTIONS} testid="vrp-programme" /></Field>
                    <Field label="Indicative budget" hint="Starting prices shown are indicative; this helps SAFE recommend an appropriate scope and is not a final quotation."><Select name="indicative_budget" value={formData.indicative_budget} onChange={handleChange} options={BUDGETS} testid="vrp-budget" /></Field>
                  </div>
                  <Field label="How did you hear about SAFE?"><Select name="referral_source" value={formData.referral_source} onChange={handleChange} options={REFERRALS} testid="vrp-referral" /></Field>
                  <Field label="Additional information" hint="Optional"><textarea name="additional_information" rows={4} value={formData.additional_information} onChange={handleChange} className={inputClass} data-testid="vrp-additional" /></Field>
                </div>

                {/* DECLARATIONS */}
                <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Declarations</h3>
                  {[
                    ['declaration_authorised', 'I confirm that I am authorised to submit this information on behalf of the organisation.'],
                    ['declaration_not_verification', 'I understand that the Verification Readiness Programme is not verification, certification, endorsement, or approval.'],
                    ['declaration_no_guarantee', 'I understand that submitting this application does not guarantee acceptance into the programme or any future SAFE Labs outcome.'],
                    ['declaration_privacy', 'I agree to the Privacy Policy and relevant application terms.'],
                  ].map(([key, text]) => (
                    <label key={key} className="flex items-start gap-3 cursor-pointer" data-testid={`vrp-${key.replace(/_/g, '-')}`}>
                      <input type="checkbox" checked={formData[key]} onChange={(e) => setFormData((p) => ({ ...p, [key]: e.target.checked }))} className="w-4 h-4 mt-1 accent-slate-900" />
                      <span className="text-sm text-slate-700 leading-relaxed">{text} <span className="text-red-500">*</span></span>
                    </label>
                  ))}
                  <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-slate-100" data-testid="vrp-marketing-opt-in">
                    <input type="checkbox" checked={formData.marketing_opt_in} onChange={(e) => setFormData((p) => ({ ...p, marketing_opt_in: e.target.checked }))} className="w-4 h-4 mt-1 accent-slate-900" />
                    <span className="text-sm text-slate-600 leading-relaxed">I would like to receive relevant SAFE Standard updates.</span>
                  </label>
                </div>
              </div>
            )}

            {/* NAVIGATION */}
            {draftSavedAt && step > 1 && (
              <div className="flex items-start gap-2 bg-cyan-50 border border-cyan-200 px-4 py-3" data-testid="vrp-draft-saved">
                <CheckCircle2 className="w-4 h-4 text-cyan-700 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-cyan-900">
                  Saved. Your answers so far are with SAFE, so you can leave this page and finish the
                  application whenever it suits you.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between gap-4 pt-2">
              {step > 1 ? (
                <button type="button" onClick={back} className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-6 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-slate-100 transition-colors" data-testid="vrp-back">
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
              ) : <span />}
              {step < 5 ? (
                <button type="button" onClick={next} className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors" data-testid="vrp-next">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="submit" disabled={submitting} className={`inline-flex items-center gap-2 bg-cyan-600 text-white px-8 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-cyan-500 transition-colors ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="vrp-submit">
                  {submitting ? 'Submitting\u2026' : 'Submit Application'}
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">Submitting an application does not commit your organisation to purchase. The final scope and price will be confirmed before any payment or contractual commitment.</p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default VerificationReadinessPage;
